import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, Link, useParams } from "react-router-dom";
import YoutubeApi from "../Api/YoutubeApi";
import ImageUploadApi from "../Api/ImageUploadApi";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL } from "../Config/Index";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";

const YoutubeFormLayer = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        url: "",
        status: true
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [errors, setErrors] = useState({});

    const statusOptions = [
        { value: true, label: "Active" },
        { value: false, label: "Inactive" },
    ];

    useEffect(() => {
        if (id) fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await YoutubeApi.getYoutubeLinkById(id);
            if (res.status) {
                const data = res.response.data;
                setFormData({
                    url: data.url || "",
                    status: data.status ?? true
                });
                if (data.image) {
                    setExistingImage(data.image);
                    setPreviewImage(`${IMAGE_BASE_URL}/${data.image.path}`);
                }
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : true }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error("Image size must be less than 2MB");
                setErrors(prev => ({ ...prev, image: "Image size must be less than 2MB" }));
                setImageFile(null);
                setPreviewImage(null);
                e.target.value = ""; // Clear file input
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPreviewImage(reader.result);
            reader.readAsDataURL(file);
            setErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setPreviewImage(null);
        setExistingImage(null);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.url?.trim()) newErrors.url = "Youtube URL is required";
        if (!imageFile && !previewImage) newErrors.image = "Thumbnail Image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            let finalImage = existingImage;
            if (imageFile) {
                const form = new FormData();
                form.append("file", imageFile);
                const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "youtube" });
                if (uploadRes.status) {
                    finalImage = uploadRes.response.data || uploadRes.response;
                } else {
                    setLoading(false);
                    return;
                }
            }

            const payload = {
                ...formData,
                image: finalImage
            };

            let res;
            if (isEditMode) {
                res = await YoutubeApi.updateYoutubeLink(id, payload);
            } else {
                res = await YoutubeApi.createYoutubeLink(payload);
            }

            if (res.status) {
                navigate("/youtube");
            }
        } catch (error) {
            console.error("Error submitting:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0" style={{ color: '#364E3D' }}>{isEditMode ? "Edit" : "Add"} Youtube Link</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit} className="pb-40">
                    <div className="row gy-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Youtube URL <span className="text-danger">*</span></label>
                            <input 
                                type="text" 
                                className={`form-control radius-8 ${errors.url ? 'is-invalid' : ''}`} 
                                name="url"
                                placeholder="Enter Youtube URL" 
                                value={formData.url}
                                onChange={handleChange}
                            />
                            {errors.url && <div className="invalid-feedback">{errors.url}</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Status <span className="text-danger">*</span></label>
                            <Select 
                                name="status" 
                                options={statusOptions} 
                                value={statusOptions.find((opt) => opt.value === formData.status)} 
                                onChange={handleSelectChange} 
                                styles={selectStyles(errors.status)} 
                                isClearable={false} 
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold">Thumbnail Image <span className="text-danger">*</span> <small className="text-secondary-light fw-normal">(Max 2MB)</small></label>
                            {!previewImage && (
                                <div className="position-relative">
                                    <input 
                                        type="file" 
                                        className="form-control radius-8" 
                                        accept="image/*" 
                                        onChange={handleImageChange} 
                                    />
                                    {errors.image && <small className="text-danger">{errors.image}</small>}
                                </div>
                            )}
                            {previewImage && (
                                <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light-600">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-100-px h-100-px rounded-8 overflow-hidden border">
                                            <img src={previewImage} alt="Preview" className="w-100 h-100 object-fit-cover" />
                                        </div>
                                    </div>
                                    <button type="button" className="btn btn-icon btn-primary-100 text-danger-600 rounded-circle" onClick={handleRemoveImage} title="Delete">
                                        <Icon icon="mingcute:delete-2-line" width="24" height="24" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-24">
                        <Link to="/youtube" className="btn btn-outline-danger-600 px-32 radius-8 justify-content-center d-flex align-items-center" style={{ width: "120px", height: "48px" }}>Cancel</Link>
                        <button type="submit" className="btn btn-primary radius-8 px-18 py-11 justify-content-center d-flex align-items-center" disabled={loading} style={{ backgroundColor: "#364e3d", borderColor: "#364e3d", width: "120px", height: "48px" }}>
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default YoutubeFormLayer;

